<script lang="ts">
  /**
   * P4: per-funnel "Events" rule editor. A friendlier projection over the SAME
   * stage matchers the graph editor uses (stage.matchers JSON) — "When <event>
   * <where…> → move to <stage>". Saving goes through updateFunnel → matchers,
   * which invalidates the compile cache server-side. No separate rule store.
   */
  import { onMount } from 'svelte';
  import Icon from './Icon.svelte';
  import { showAlert } from '../lib/ui-alert';
  import { fetchFunnel, updateFunnel, type FunnelDetail, type FunnelSaveStage } from '../lib/funnels-api';
  import { fetchPublicDataTables, fetchPublicDataTable, type ApiField } from '../lib/data-tables-api';

  let { slug, onClose }: { slug: string; onClose: () => void } = $props();

  type Matcher = Record<string, unknown> & { kind: string };
  interface StageRow {
    slug: string;
    name: string;
    ordinal: number;
    positionX?: number;
    positionY?: number;
    isEntry?: boolean;
    isExit?: boolean;
    matchers: Matcher[];
  }

  let funnel = $state<FunnelDetail | null>(null);
  let stages = $state<StageRow[]>([]);
  let edges = $state<{ from: string; to: string }[]>([]);
  let customEvents = $state<{ key: string; name: string }[]>([]);
  let loading = $state(true);
  let saving = $state(false);

  // Table/field pickers for the "entity_field_changed" rule.
  let tables = $state<{ slug: string; name: string }[]>([]);
  let fieldsBySlug = $state<Record<string, ApiField[]>>({});

  /** matcher.entityType (CONTACT/PAYMENT/<SLUG>) ↔ table slug (users/payments/slug). */
  function slugForEntityType(et: unknown): string {
    const s = String(et ?? '').trim();
    if (!s) return '';
    const u = s.toUpperCase();
    if (u === 'CONTACT') return 'users';
    if (u === 'PAYMENT') return 'payments';
    return s.toLowerCase();
  }
  function entityTypeForSlug(slug: string): string {
    if (slug === 'users') return 'CONTACT';
    if (slug === 'payments') return 'PAYMENT';
    return slug.toUpperCase();
  }
  async function ensureFields(slug: string) {
    if (!slug || fieldsBySlug[slug]) return;
    try {
      const t = await fetchPublicDataTable(slug);
      fieldsBySlug = { ...fieldsBySlug, [slug]: t.fields ?? [] };
    } catch { /* ignore — falls back to free-text */ }
  }
  function fieldDef(slug: string, key: unknown): ApiField | undefined {
    return (fieldsBySlug[slug] ?? []).find((f) => f.fieldKey === String(key ?? ''));
  }
  function onPickTable(m: Matcher, slug: string) {
    m.entityType = slug ? entityTypeForSlug(slug) : undefined;
    m.field = '';
    m.equals = undefined;
    stages = stages;
    void ensureFields(slug);
  }
  function onPickField(m: Matcher, key: string) {
    m.field = key;
    m.equals = undefined;
    stages = stages;
  }

  // ── Custom events (funnel-scoped). `key` = the matched/emitted event type. ──
  function eventSlug(name: string): string {
    return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'event';
  }
  function newEventKey(name: string): string {
    const base = `cev.${slug}.${eventSlug(name)}`;
    const taken = new Set(customEvents.map((e) => e.key));
    let k = base;
    let i = 1;
    while (taken.has(k)) k = `${base}-${i++}`;
    return k;
  }
  /** Create a custom event from a name; returns its key (or null if cancelled/empty). */
  function createCustomEvent(name: string): string | null {
    const n = name.trim();
    if (!n) return null;
    const existing = customEvents.find((e) => e.name.trim().toLowerCase() === n.toLowerCase());
    if (existing) return existing.key;
    const key = newEventKey(n);
    customEvents = [...customEvents, { key, name: n }];
    return key;
  }
  function renameCustomEvent(key: string, name: string) {
    customEvents = customEvents.map((e) => (e.key === key ? { ...e, name } : e));
  }
  function removeCustomEvent(key: string) {
    customEvents = customEvents.filter((e) => e.key !== key);
    // Cascade: strip any stage rules that referenced this event.
    for (const st of stages) st.matchers = st.matchers.filter((m) => !(m.kind === 'custom_event_eq' && m.eventKey === key));
    stages = stages;
  }
  function eventName(key: unknown): string {
    return customEvents.find((e) => e.key === String(key ?? ''))?.name ?? String(key ?? '');
  }
  /** Унікальна назва за замовчуванням для швидкого створення з правила. */
  function defaultEventName(): string {
    const base = 'Нова подія';
    const taken = new Set(customEvents.map((e) => e.name.trim().toLowerCase()));
    if (!taken.has(base.toLowerCase())) return base;
    let i = 2;
    while (taken.has(`${base} ${i}`.toLowerCase())) i++;
    return `${base} ${i}`;
  }
  /** Inline-додавання у блоці «Кастомні події» (без нативного prompt). */
  let newEventName = $state('');
  function addEventInline() {
    if (createCustomEvent(newEventName)) newEventName = '';
  }

  /** Synthetic "Коли" token for a matcher (drives the dropdown). */
  function whenToken(m: Matcher): string {
    if (m.kind === 'entity_field_changed') return 'field';
    if (m.kind === 'custom_event_eq') return `ce:${m.eventKey}`;
    return 'legacy';
  }
  function onPickWhen(st: StageRow, idx: number, token: string) {
    if (token === 'field') {
      st.matchers[idx] = defaultMatcher('entity_field_changed');
    } else if (token === '__new__') {
      // Створюємо подію з дефолтною назвою (перейменувати — у блоці «Кастомні події»).
      const key = createCustomEvent(defaultEventName());
      if (key) st.matchers[idx] = { kind: 'custom_event_eq', eventKey: key };
    } else if (token.startsWith('ce:')) {
      st.matchers[idx] = { kind: 'custom_event_eq', eventKey: token.slice(3) };
    }
    stages = stages;
  }

  onMount(async () => {
    try {
      const f = await fetchFunnel(slug);
      funnel = f;
      customEvents = (f.customEvents ?? []).map((e) => ({ ...e }));
      stages = f.stages
        .slice()
        .sort((a, b) => a.ordinal - b.ordinal)
        .map((s) => ({
          slug: s.slug,
          name: s.name,
          ordinal: s.ordinal,
          positionX: s.positionX ?? undefined,
          positionY: s.positionY ?? undefined,
          isEntry: s.isEntry,
          isExit: s.isExit,
          matchers: ((s.matchers as Matcher[]) ?? []).map((m) => ({ ...m })),
        }));
      edges = f.edges.map((e) => ({ from: e.from, to: e.to }));
      // Tables for the picker + preload fields for existing field-change rules.
      try {
        tables = (await fetchPublicDataTables()).items.map((t) => ({ slug: t.slug, name: t.name }));
      } catch { /* ignore */ }
      for (const st of stages)
        for (const m of st.matchers)
          if (m.kind === 'entity_field_changed' && m.entityType) void ensureFields(slugForEntityType(m.entityType));
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      loading = false;
    }
  });

  function defaultMatcher(kind: string): Matcher {
    if (kind === 'custom_event_eq') return { kind, eventKey: '' };
    return { kind: 'entity_field_changed', field: '' };
  }
  function addRule(st: StageRow) {
    // Default to a custom event if any exist, else field-change.
    st.matchers = [...st.matchers, customEvents[0] ? { kind: 'custom_event_eq', eventKey: customEvents[0].key } : defaultMatcher('entity_field_changed')];
    stages = stages;
  }
  function removeRule(st: StageRow, idx: number) {
    st.matchers = st.matchers.filter((_, i) => i !== idx);
    stages = stages;
  }

  // ── Stage management (this editor fully replaces the graph builder) ──────────
  function slugify(s: string): string {
    return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 50);
  }
  function uniqueSlug(base: string): string {
    const taken = new Set(stages.map((s) => s.slug));
    let s = base || 'stage';
    let i = 1;
    while (taken.has(s)) s = `${base}-${i++}`;
    return s;
  }
  function addStage() {
    const slug = uniqueSlug(`stage-${stages.length + 1}`);
    stages = [...stages, { slug, name: 'Новий стейдж', ordinal: stages.length, isEntry: stages.length === 0, isExit: false, matchers: [] }];
  }
  function removeStage(idx: number) {
    stages = stages.filter((_, i) => i !== idx);
  }
  function moveStage(idx: number, dir: -1 | 1) {
    const j = idx + dir;
    if (j < 0 || j >= stages.length) return;
    const arr = [...stages];
    [arr[idx], arr[j]] = [arr[j]!, arr[idx]!];
    stages = arr;
  }

  async function save() {
    if (!funnel) return;
    if (stages.length === 0) return showAlert('Додайте хоча б один стейдж.');
    for (const st of stages) {
      if (!st.name.trim()) return showAlert('Кожен стейдж потребує назви.');
      for (const m of st.matchers) {
        if (m.kind === 'entity_field_changed' && !String(m.field ?? '').trim()) return showAlert(`Стейдж «${st.name}»: вкажіть поле.`);
        if (m.kind === 'custom_event_eq' && !String(m.eventKey ?? '').trim()) return showAlert(`Стейдж «${st.name}»: оберіть кастомну подію.`);
      }
    }
    saving = true;
    try {
      // Ordinal = position in the list (linear pipeline order).
      const saveStages: FunnelSaveStage[] = stages.map((s, idx) => ({
        slug: s.slug,
        name: s.name.trim(),
        ordinal: idx,
        matchers: s.matchers,
        positionX: s.positionX,
        positionY: s.positionY,
        isEntry: s.isEntry,
        isExit: s.isExit,
      }));
      // Keep only edges whose both ends still exist (graph builder is gone, but
      // pre-existing graph funnels keep their edges).
      const slugs = new Set(stages.map((s) => s.slug));
      const keptEdges = edges.filter((e) => slugs.has(e.from) && slugs.has(e.to));
      await updateFunnel(slug, { stages: saveStages, edges: keptEdges, customEvents });
      onClose();
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      saving = false;
    }
  }
</script>

<div class="fixed inset-0 z-50 bg-black/30 flex items-stretch justify-end">
  <div class="w-full max-w-[640px] bg-[var(--color-bg-2)] h-full overflow-y-auto shadow-xl">
    <div class="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-[var(--color-border-3)] bg-white/90 backdrop-blur px-5 py-3">
      <div class="flex items-center gap-2">
        <Icon name="funnels" size={16} />
        <div>
          <div class="text-[13px] font-bold">Стейджі та події</div>
          <div class="text-[10.5px] text-[var(--color-text-muted)]">{funnel?.name ?? slug}</div>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <button onclick={addStage} class="rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs font-semibold text-[var(--color-pu-3)] hover:bg-[var(--color-bg-3)]">+ стейдж</button>
        <button onclick={save} disabled={saving} class="rounded-md bg-[var(--color-pu)] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">{saving ? 'Збереження…' : 'Зберегти'}</button>
        <button onclick={onClose} class="rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs">×</button>
      </div>
    </div>

    <div class="p-5">
      <p class="text-[11px] text-[var(--color-text-muted)] mb-4">
        Кожне правило: <strong>коли</strong> стається подія (з умовами) — сутність <strong>переходить на стейдж</strong>, якому належить правило.
      </p>

      {#if !loading}
        <!-- Кастомні події воронки -->
        <div class="rounded-xl border border-[var(--color-border-3)] bg-white p-3.5 mb-4">
          <div class="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)] mb-2">Кастомні події</div>
          {#if customEvents.length === 0}
            <div class="text-[10.5px] italic text-[var(--color-text-muted-2)] mb-2">Ще немає подій. Створіть подію — її можна буде «додавати» картці автоматизацією та ловити правилом стейджа.</div>
          {:else}
            <div class="flex flex-col gap-1.5 mb-2">
              {#each customEvents as ce (ce.key)}
                <div class="flex items-center gap-2">
                  <input
                    value={ce.name}
                    oninput={(e) => renameCustomEvent(ce.key, (e.currentTarget as HTMLInputElement).value)}
                    class="flex-1 rounded-md border border-[var(--color-border-3)] px-2 py-1 text-[11px]"
                  />
                  <code class="text-[9.5px] text-[var(--color-text-muted-2)] truncate max-w-[180px]" title={ce.key}>{ce.key}</code>
                  <button onclick={() => removeCustomEvent(ce.key)} title="Видалити подію" class="text-red-600 hover:bg-red-50 rounded p-1"><Icon name="delete" size={13} /></button>
                </div>
              {/each}
            </div>
          {/if}
          <!-- inline add (без нативного prompt) -->
          <div class="flex items-center gap-2">
            <input
              bind:value={newEventName}
              onkeydown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEventInline(); } }}
              placeholder="Назва нової події, напр. Передзвонив"
              class="flex-1 rounded-md border border-[var(--color-border-3)] px-2 py-1 text-[11px]"
            />
            <button
              onclick={addEventInline}
              disabled={!newEventName.trim()}
              class="rounded-md border border-[var(--color-border-3)] px-2.5 py-1 text-[10.5px] font-semibold text-[var(--color-pu-3)] hover:bg-[var(--color-bg-3)] disabled:opacity-40"
            >+ Додати</button>
          </div>
        </div>
      {/if}

      {#if loading}
        <div class="py-20 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
      {:else if stages.length === 0}
        <div class="rounded-xl border border-dashed border-[var(--color-border-3)] bg-white p-8 text-center text-[12px] text-[var(--color-text-muted)]">
          Немає стейджів. <button onclick={addStage} class="text-[var(--color-pu-3)] font-semibold hover:underline">Додати перший</button>
        </div>
      {:else}
        <div class="flex flex-col gap-3">
          {#each stages as st, sIdx (st.slug)}
            <div class="rounded-xl border border-[var(--color-border-3)] bg-white p-3.5">
              <div class="flex items-center gap-2 mb-2">
                <div class="flex flex-col leading-none">
                  <button onclick={() => moveStage(sIdx, -1)} disabled={sIdx === 0} title="Вгору" class="text-[var(--color-text-muted-2)] hover:text-[var(--color-pu-3)] disabled:opacity-30 text-[10px]">▲</button>
                  <button onclick={() => moveStage(sIdx, 1)} disabled={sIdx === stages.length - 1} title="Вниз" class="text-[var(--color-text-muted-2)] hover:text-[var(--color-pu-3)] disabled:opacity-30 text-[10px]">▼</button>
                </div>
                <span class="text-[12px] font-bold text-[var(--color-pu-3)]">{sIdx}.</span>
                <input bind:value={st.name} placeholder="Назва стейджа" class="flex-1 rounded-md border border-[var(--color-border-3)] px-2 py-1 text-[12px] font-semibold" />
                <button onclick={() => addRule(st)} class="rounded-md border border-[var(--color-border-3)] px-2 py-1 text-[10.5px] font-semibold text-[var(--color-pu-3)] hover:bg-[var(--color-bg-3)] whitespace-nowrap">+ Правило</button>
                <button onclick={() => removeStage(sIdx)} title="Видалити стейдж" class="text-red-600 hover:bg-red-50 rounded p-1"><Icon name="delete" size={14} /></button>
              </div>
              {#if st.matchers.length === 0}
                <div class="text-[10.5px] italic text-[var(--color-text-muted-2)]">Немає правил — на цей стейдж не можна потрапити автоматично.</div>
              {/if}
              <div class="flex flex-col gap-2">
                {#each st.matchers as m, idx (idx)}
                  {@const token = whenToken(m)}
                  <div class="rounded-lg border border-[var(--color-border-3)] bg-[var(--color-bg-3)] p-2.5">
                    <div class="flex items-center gap-2 mb-1.5">
                      <span class="text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)]">Коли</span>
                      <select
                        value={token}
                        onchange={(e) => onPickWhen(st, idx, (e.currentTarget as HTMLSelectElement).value)}
                        class="flex-1 rounded-md border border-[var(--color-border-3)] bg-white px-2 py-1 text-[11px]"
                      >
                        {#if token === 'legacy'}<option value="legacy" disabled>(старе правило: {m.kind})</option>{/if}
                        <option value="field">Змінилось поле запису</option>
                        {#each customEvents as ce (ce.key)}<option value={`ce:${ce.key}`}>{ce.name}</option>{/each}
                        <option value="__new__">+ Нова кастомна подія…</option>
                      </select>
                      <button onclick={() => removeRule(st, idx)} title="Видалити" class="text-red-600 hover:bg-red-50 rounded px-1.5 py-1"><Icon name="close" size={13} /></button>
                    </div>
                    <!-- per-kind fields -->
                    {#if m.kind === 'custom_event_eq'}
                      <div class="text-[10.5px] text-[var(--color-text-muted)]">Подія «{eventName(m.eventKey)}» додана картці → перехід на цей стейдж.</div>
                    {:else if m.kind !== 'entity_field_changed'}
                      <div class="text-[10.5px] italic text-[var(--color-text-muted-2)]">Старе правило ({m.kind}) — працює, але редагування лише через зміну на нове.</div>
                    {:else if m.kind === 'entity_field_changed'}
                      {@const tslug = slugForEntityType(m.entityType)}
                      {@const fd = fieldDef(tslug, m.field)}
                      {@const opts = fd?.options ?? []}
                      <div class="grid grid-cols-3 gap-2">
                        <!-- table -->
                        <select
                          value={tslug}
                          onchange={(e) => onPickTable(m, (e.currentTarget as HTMLSelectElement).value)}
                          class="rounded-md border border-[var(--color-border-3)] bg-white px-2 py-1 text-[11px]"
                        >
                          <option value="">— будь-яка таблиця —</option>
                          {#each tables as t (t.slug)}<option value={t.slug}>{t.name}</option>{/each}
                        </select>
                        <!-- field -->
                        {#if tslug && fieldsBySlug[tslug]}
                          <select
                            value={String(m.field ?? '')}
                            onchange={(e) => onPickField(m, (e.currentTarget as HTMLSelectElement).value)}
                            class="rounded-md border border-[var(--color-border-3)] bg-white px-2 py-1 text-[11px]"
                          >
                            <option value="">— поле —</option>
                            {#each fieldsBySlug[tslug] as f (f.fieldKey)}<option value={f.fieldKey}>{f.label}</option>{/each}
                          </select>
                        {:else}
                          <input bind:value={m.field} placeholder="поле, напр. status" class="rounded-md border border-[var(--color-border-3)] px-2 py-1 text-[11px] font-mono" />
                        {/if}
                        <!-- value: select if the field has static options, else free text -->
                        {#if opts.length > 0}
                          <select bind:value={m.equals} class="rounded-md border border-[var(--color-border-3)] bg-white px-2 py-1 text-[11px]">
                            <option value={undefined}>— будь-яке —</option>
                            {#each opts as o (o)}<option value={o}>{o}</option>{/each}
                          </select>
                        {:else}
                          <input bind:value={m.equals} placeholder="нове значення (опц.)" class="rounded-md border border-[var(--color-border-3)] px-2 py-1 text-[11px]" />
                        {/if}
                      </div>
                    {/if}
                    <div class="mt-1.5 text-[10px] text-[var(--color-text-muted-2)]">→ перейти на «{st.name}»</div>
                  </div>
                {/each}
              </div>
            </div>
          {/each}
        </div>
      {/if}
    </div>
  </div>
</div>
